import { SectionHeader } from '../common/section-header'
import { Badge, Notice } from '@speaking-coach/ui'
import { z } from 'zod'

const perceivedVoice = z.enum(['female', 'male'])
const accentFamily = z.enum(['american', 'australian', 'british', 'indian', 'japanese'])
const presentationSchema = z.object({ perceivedVoice, accentFamily, toeicStyle: z.boolean() })
const savedPresentationSchema = z.object({ perceived_voice: perceivedVoice, accent_family: accentFamily, toeic_style: z.boolean() }).transform(value => ({ perceivedVoice: value.perceived_voice, accentFamily: value.accent_family, toeicStyle: value.toeic_style }))
export type TutorPresentation = z.infer<typeof presentationSchema>
type PresentationState = { kind: 'recorded'; value: TutorPresentation } | { kind: 'missing' } | { kind: 'unavailable' }
const missing = 'Not recorded in this revision'
const unavailable = 'Presentation unavailable: this revision cannot be read.'
const panel = 'space-y-4 rounded-token-card border border-border-default bg-surface-elev p-5'
const voices: Record<TutorPresentation['perceivedVoice'], string> = { female: 'Female', male: 'Male' }
const accents: Record<TutorPresentation['accentFamily'], string> = { american: 'American', australian: 'Australian', british: 'British', indian: 'Indian', japanese: 'Japanese' }
const fields = [
  { key: 'perceivedVoice', label: 'Perceived voice' },
  { key: 'accentFamily', label: 'Accent family' },
  { key: 'toeicStyle', label: 'TOEIC-style delivery' },
] as const

type Facet = typeof fields[number]['key']
function state(value: unknown, saved: boolean): PresentationState {
  if (value === null || value === undefined) return { kind: 'missing' }
  const parsed = (saved ? savedPresentationSchema : presentationSchema).safeParse(value)
  return parsed.success ? { kind: 'recorded', value: parsed.data } : { kind: 'unavailable' }
}
type CurrentPresentationState = PresentationState | { kind: 'no-active-revision' }
export function readTutorPresentation(value: unknown, readable = true, hasActiveRevision = true): CurrentPresentationState {
  if (!hasActiveRevision) return { kind: 'no-active-revision' }
  return readable ? state(value, false) : { kind: 'unavailable' }
}
export function readRevisionTutorPresentation(payload: unknown, readable: boolean | null): PresentationState {
  if (!readable) return { kind: 'unavailable' }
  const parsed = z.object({ presentation: z.unknown().optional() }).safeParse(payload)
  return parsed.success ? state(parsed.data.presentation, true) : { kind: 'unavailable' }
}
function label(value: TutorPresentation, key: Facet): string {
  return key === 'perceivedVoice' ? voices[value.perceivedVoice] : key === 'accentFamily' ? accents[value.accentFamily] : value.toeicStyle ? 'Yes' : 'No'
}
export function TutorPresentationPanel({ presentation }: { presentation: CurrentPresentationState }) {
  return <section className={panel} aria-labelledby="tutor-presentation-heading">
    <SectionHeader id="tutor-presentation-heading" title="Tutor presentation" />
    {presentation.kind === 'no-active-revision' ? <Notice variant="neutral">No current published revision.</Notice> : presentation.kind === 'unavailable' ? <Notice variant="danger">{unavailable}</Notice> : presentation.kind === 'missing' ? <Notice variant="neutral">{missing}</Notice> : <dl className="grid gap-4 text-body sm:grid-cols-3">
      {fields.map(field => <div key={field.key}><dt className="text-text-secondary">{field.label}</dt><dd className="mt-1 text-text-primary">{label(presentation.value, field.key)}</dd></div>)}
    </dl>}
  </section>
}
export function TutorPresentationComparison({ before, after, beforeRevision, afterRevision }: { before: PresentationState; after: PresentationState; beforeRevision: number; afterRevision: number }) {
  return <section className={panel} aria-labelledby="tutor-presentation-comparison-heading">
    <SectionHeader id="tutor-presentation-comparison-heading" title="Tutor presentation comparison" />
    {before.kind === 'unavailable' || after.kind === 'unavailable' ? <Notice variant="danger">At least one revision's presentation is unavailable, so its facets cannot be compared.</Notice> : <div className="space-y-3">
      {fields.map(field => {
        const left = before.kind === 'recorded' ? before.value[field.key] : undefined
        const right = after.kind === 'recorded' ? after.value[field.key] : undefined
        const change = left === right ? 'unchanged' : left === undefined ? 'added' : right === undefined ? 'removed' : 'changed'
        return <article key={field.key} aria-label={field.label} className="rounded border border-border-default bg-surface-app p-4">
          <SectionHeader
            level={3}
            title={field.label}
            meta={<Badge variant={change === 'added' ? 'success' : change === 'removed' ? 'warning' : change === 'unchanged' ? 'muted' : 'neutral'}>{change}</Badge>}
          />
          <dl className="mt-3 grid gap-3 text-body sm:grid-cols-2">
            <div><dt className="text-caption text-text-secondary">Revision {beforeRevision}</dt><dd className="mt-1 text-text-primary">{before.kind === 'recorded' ? label(before.value, field.key) : missing}</dd></div>
            <div><dt className="text-caption text-text-secondary">Revision {afterRevision}</dt><dd className="mt-1 text-text-primary">{after.kind === 'recorded' ? label(after.value, field.key) : missing}</dd></div>
          </dl>
        </article>
      })}
    </div>}
  </section>
}
